import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ListChecks, EyeSlash } from "@phosphor-icons/react";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { fmtDate } from "@/lib/constants";

/**
 * Iter-45: Masked lead list for media buyers.
 *
 * Shows only: 8-char reference id, landing page, firm name, sub_id, state,
 * status, delivery status, sandbox flag, timestamp. No PII, no financials.
 */
const WINDOWS = [7, 30, 90];

export default function MediaBuyerLeads() {
  const [days, setDays] = useState(30);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/media-buyer/leads?days=${days}&limit=500`)
      .then((r) => setRows(r.data?.leads || []))
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load leads"))
      .finally(() => setLoading(false));
  }, [days]);

  return (
    <div className="p-2" data-testid="media-buyer-leads">
      <div className="flex items-center gap-3 mb-2 flex-wrap">
        <ListChecks size={22} weight="bold" />
        <h1 className="font-chivo font-black text-3xl tracking-tighter uppercase">
          Leads
        </h1>
        <span className="label-mono ml-2 inline-flex items-center gap-1">
          <EyeSlash size={11} weight="bold" /> PII masked · reference-only view
        </span>
        <div className="ml-auto flex gap-1">
          {WINDOWS.map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              data-testid={`mb-leads-window-${d}`}
              className={`border border-black px-3 py-1.5 text-[10px] uppercase tracking-widest ${
                days === d ? "bg-black text-white" : "hover:bg-zinc-100"
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>
      <p className="text-sm text-zinc-600 mb-6 max-w-3xl">
        Media buyers see reference IDs, attribution, and status only. Names,
        phones, and emails are handled by the buyer intake team — reach out
        to your admin for per-lead follow up.
      </p>

      <div className="brut-card overflow-hidden">
        <div className="border-b border-black px-4 py-3 flex items-center gap-2">
          <div className="font-chivo font-black uppercase tracking-tighter">
            Attributed leads
          </div>
          <div className="label-mono ml-auto">
            {rows.length} shown · last {days}d
          </div>
        </div>
        {loading ? (
          <div className="label-mono text-center py-10">Loading…</div>
        ) : !rows.length ? (
          <div className="label-mono text-center py-10 text-zinc-500">
            No leads have flowed through a custom landing page yet.
          </div>
        ) : (
          <>
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-xs" data-testid="mb-leads-table">
            <thead>
              <tr className="border-b-2 border-black bg-zinc-100 text-[10px] uppercase tracking-widest">
                <th className="text-left py-2 px-3">Reference</th>
                <th className="text-left py-2 px-3">Firm</th>
                <th className="text-left py-2 px-3">Page</th>
                <th className="text-left py-2 px-3">sub_id</th>
                <th className="text-left py-2 px-3">State</th>
                <th className="text-left py-2 px-3">Status</th>
                <th className="text-left py-2 px-3">Delivery</th>
                <th className="text-left py-2 px-3">Sandbox</th>
                <th className="text-left py-2 px-3">When</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((l) => (
                <tr key={l.reference + l.created_at}
                    className="border-b border-zinc-200 hover:bg-zinc-50"
                    data-testid={`mb-leads-row-${l.reference}`}>
                  <td className="py-2 px-3 font-mono font-bold">#{l.reference}</td>
                  <td className="py-2 px-3">{l.firm_name}</td>
                  <td className="py-2 px-3 font-mono text-[11px]">{l.landing_page || "—"}</td>
                  <td className="py-2 px-3 font-mono text-[11px]">{l.sub_id || "—"}</td>
                  <td className="py-2 px-3 font-mono">{l.state || "—"}</td>
                  <td className="py-2 px-3">
                    <StatusBadge status={l.status} testId={`mb-leads-status-${l.reference}`} />
                  </td>
                  <td className="py-2 px-3 font-mono text-[11px] text-zinc-600">{l.delivery_status || "—"}</td>
                  <td className="py-2 px-3">
                    {l.is_test ? (
                      <span className="text-[9px] uppercase tracking-widest bg-amber-100 border border-amber-800 text-amber-900 px-1.5 py-0.5">
                        sandbox
                      </span>
                    ) : "—"}
                  </td>
                  <td className="py-2 px-3 text-[11px] text-zinc-600">{fmtDate(l.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          {/* Mobile cards */}
          <div className="md:hidden p-2 flex flex-col gap-2" data-testid="mb-leads-cards">
            {rows.map((l) => (
              <div key={l.reference + l.created_at} className="border border-black bg-white p-3" data-testid={`mb-leads-card-${l.reference}`}>
                <div className="flex items-center gap-2 mb-1 pb-1 border-b border-zinc-200">
                  <div className="font-mono font-bold text-sm">#{l.reference}</div>
                  <StatusBadge status={l.status} testId={`mb-leads-status-mobile-${l.reference}`} />
                  {l.is_test && (
                    <span className="ml-auto text-[9px] uppercase tracking-widest bg-amber-100 border border-amber-800 text-amber-900 px-1.5 py-0.5">sandbox</span>
                  )}
                </div>
                <div className="text-sm font-bold truncate">{l.firm_name}</div>
                <div className="grid grid-cols-2 gap-x-3 text-[11px] font-mono text-zinc-700 mt-1">
                  <div><span className="label-mono">Page:</span> {l.landing_page || "—"}</div>
                  <div><span className="label-mono">State:</span> {l.state || "—"}</div>
                  <div><span className="label-mono">sub_id:</span> {l.sub_id || "—"}</div>
                  <div><span className="label-mono">Delivery:</span> {l.delivery_status || "—"}</div>
                </div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-1">{fmtDate(l.created_at)}</div>
              </div>
            ))}
          </div>
          </>
        )}
      </div>
    </div>
  );
}
